import _ from 'lodash';
import React, { useMemo, useCallback }  from 'react';
import { ListGroup, ListGroupItem } from 'reactstrap';
import { cx, PropTypes, useSpatialNav, Icon } from '~/utils';
import { log } from '@swiper/core';

const addItemFlag = Symbol( 'addItem' );

export function PickList( props ) {
  const {
    items, renderItem, onPick, selectors, startAt,
    onAdd, addLabel, noContent,
  } = props;

  const selectorList = useMemo( () => {
    if ( ! selectors ) return [];
    let sel = selectors;
    let start = startAt;
    if ( sel === 'numbers' ) sel = '123456789'.split( '' );
    if ( sel === 'letters' ) sel = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split( '' );
    if ( _.isString( start ) ) start = _.findIndex( sel, start );
    if ( _.isFinite( start ) ) return sel.slice( start );
    return sel;
  }, [ selectors, startAt ] );

  const pickItem = useCallback( ( item ) => {
    log.debug( 'PICK ITEM', item );
    if ( item === addItemFlag ) return onAdd();
    return onPick( item );
  }, [ onPick, onAdd ] );

  const kidsList = useMemo( () => {
    return _.map( items, ( item, idx ) => {
      const shortcut = item.shortcut
        ? item.shortcut.toUpperCase()
        : selectorList[ idx ];
      return { shortcut, ...item };
    } );
  }, [ items, selectorList ] );

  useSpatialNav( useCallback( ( key, ev ) => {
    const item = _.find( kidsList, { shortcut : key.toUpperCase() } );
    if ( ! item ) return false;
    return pickItem( item );
  }, [ kidsList, pickItem ] ) );

  const kids = useMemo( () => {
    // if ( ! selectorList ) return [];
    const res = _.map( items, ( item, idx ) => {
      const shortcut = item.shortcut || selectorList[ idx ];
      return (
        <PickListItem
          key={ item.id || item.label || idx }
          item={ item }
          shortcut={ shortcut }
          body={ renderItem( item ) }
          onClick={ pickItem }
        />
      );
    } );
    if ( _.isFunction( onAdd ) ) {
      res.push( <PickListItem
        key='--add--'
        item={addItemFlag}
        shortcut={selectorList[ res.length ]}
        body={addLabel}
        onClick={pickItem}
      /> );
    }
    return res;
  }, [ pickItem, items, renderItem, selectorList, addLabel, onAdd ] );

  const classes = cx(
    'pick-list', props.className,
    props.title && `picker-${_.kebabCase( props.title )}`,
    { 'no-content' : !kids.length },
  );

  if ( kids.length || noContent ) {
    return (
      <div className={classes}>
        { props.title && <h1>{ props.title }</h1> }
        <ListGroup className="picker">
          {kids.length ? kids : noContent}
        </ListGroup>
      </div>
    );
  } else {
    return null;
  }
}
PickList.propTypes = {
  items       : PropTypes.arrayOf( PropTypes.shape( {
    label       : PropTypes.string.isRequired,
    ...PickListItem.propTypes,
  } ) ).isRequired,
  onChange    : PropTypes.func,
  onPick      : PropTypes.func,
  renderItem  : PropTypes.func.isRequired,
  title       : PropTypes.string,
  pad         : PropTypes.number,
  active      : PropTypes.bool,
  selectors   : PropTypes.oneOfType( [
    PropTypes.oneOf( [ 'letters', 'numbers' ] ),
    PropTypes.arrayOf( PropTypes.string ),
  ] ),
  startAt     : PropTypes.oneOfType( [ PropTypes.string, PropTypes.number ] ),
  className   : PropTypes.className,
  noContent   : PropTypes.node,
};
PickList.defaultProps = {
  renderItem  : item => {
    if ( _.isString( item.label ) ) {
      return (
        <span>
          {item.label}
          {item.icon && <Icon icon={item.icon} pull="right" />}
        </span>
      );
    } else {
      return JSON.stringify( item );
    }
  },
};

function PickListItem( props_in ) {
  const { body, shortcut, className, item, onClick, ...props } = props_in;
  props.className = cx( 'focusable', className );
  props.onClick = useCallback( () => onClick( item ), [ item, onClick ] );
  const lgi = (
    <ListGroupItem {...props}>
      { shortcut && ( <kbd className="mr-2">{shortcut}</kbd> ) }
      {body}
    </ListGroupItem>
  );
  return lgi;
}
PickListItem.propTypes = {
  body      : PropTypes.node,
  shortcut  : PropTypes.string,
  className : PropTypes.className,
  onClick   : PropTypes.func,
  onAdd     : PropTypes.func,
  addLabel  : PropTypes.node,
};
PickListItem.defaultProps = {
  // children : '&nbsp;'
};

